
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp,
  Users,
  Play,
  Eye,
  Download,
  RefreshCw,
  Activity,
  Globe
} from 'lucide-react';
import { useAnalytics } from '@/lib/analytics/tracker';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { UserGrowthChart } from '@/components/admin/user-growth-chart';

// Analytics data interface
interface AnalyticsData {
  summary: {
    totalEvents: number;
    uniqueUsers: number;
    pageViews: number;
    streamViews: number;
    avgSessionDuration: number;
    bounceRate: number;
  };
  topPages: Array<{ url: string; views: number }>;
  topStreams: Array<{ streamId: number; title: string; views: number }>;
  userGrowth: Array<{ date: string; users: number }>;
  geoDistribution: Array<{ country: string; users: number; percentage: number }>;
  deviceStats: { desktop: number; mobile: number; tablet: number };
}

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  description?: string;
  trend?: 'up' | 'down' | 'neutral';
}

function MetricCard({ title, value, change, icon, description, trend }: MetricCardProps) {
  const formatChange = (change?: number) => {
    if (!change) return null;
    const isPositive = change > 0;
    const color = isPositive ? 'text-green-600' : 'text-red-600';
    const arrow = isPositive ? '↑' : '↓';
    return (
      <span className={`text-sm ${color} ml-2`}>
        {arrow} {Math.abs(change)}%
      </span>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium truncate flex-1 min-w-0">{title}</CardTitle>
        <div className="text-muted-foreground flex-shrink-0">{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value.toLocaleString()}</div>
        <div className="flex items-center text-xs text-muted-foreground">
          {description && <span>{description}</span>}
          {formatChange(change)}
        </div>
      </CardContent>
    </Card>
  );
}

export function AnalyticsDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('7d');
  const [isLoading, setIsLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [selectedStream, setSelectedStream] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const { getSessionData } = useAnalytics();

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setIsLoading(true);
      try {
        let endpoint = `api/analytics/summary?period=${timeRange}`;
        if (selectedStream !== 'all') endpoint += `&streamId=${selectedStream}`;
        if (selectedRole !== 'all') endpoint += `&role=${selectedRole}`;
        
        const response = await api.get<AnalyticsData>(endpoint);

        if (response.success && response.data) {
          setAnalyticsData(response.data);
        } else {
          toast.error('Failed to load analytics data');
        }
      } catch (error) {
        console.error('Analytics fetch error:', error);
        toast.error('Failed to load analytics data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, [timeRange, selectedStream, selectedRole]);

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<AnalyticsData>(`api/analytics/summary?period=${timeRange}`);
      if (response.success && response.data) {
        setAnalyticsData(response.data);
        toast.success('Analytics refreshed');
      } else {
        toast.error('Failed to refresh analytics');
      }
    } catch {
      toast.error('Failed to refresh analytics');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = () => {
    if (!analyticsData) {
      toast.error('No analytics data to export');
      return;
    }
    const csv = [
      ['Metric', 'Value'],
      ['Total Events', analyticsData.summary.totalEvents],
      ['Unique Users', analyticsData.summary.uniqueUsers],
      ['Page Views', analyticsData.summary.pageViews],
      ['Stream Views', analyticsData.summary.streamViews],
      ['Avg Session Duration (s)', analyticsData.summary.avgSessionDuration],
      ['Bounce Rate (%)', analyticsData.summary.bounceRate],
      ['Period', timeRange],
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${timeRange}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Analytics exported as CSV');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor platform performance and user engagement
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={selectedStream} onValueChange={setSelectedStream}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All Streams" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Streams</SelectItem>
              <SelectItem value="live">Live Now</SelectItem>
              <SelectItem value="recorded">Recorded</SelectItem>
            </SelectContent>
          </Select>

          <Select value={selectedRole} onValueChange={setSelectedRole}>
            <SelectTrigger className="w-32">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
              <SelectItem value="pastor">Pastors</SelectItem>
              <SelectItem value="media_team">Media Team</SelectItem>
              <SelectItem value="member">Members</SelectItem>
            </SelectContent>
          </Select>

          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1d">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>

          <Button onClick={handleExport}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-4 rounded" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16 mb-2" />
                <Skeleton className="h-3 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <MetricCard
            title="Total Users"
            value={analyticsData?.summary.uniqueUsers || 0}
            icon={<Users className="h-4 w-4" />}
            description="Active community members"
          />
          <MetricCard
            title="Active Streams"
            value={analyticsData?.summary.streamViews || 0}
            icon={<Play className="h-4 w-4" />}
            description="Stream views this period"
          />
          <MetricCard
            title="Page Views"
            value={analyticsData?.summary.pageViews || 0}
            icon={<Eye className="h-4 w-4" />}
            description="Total page views"
          />
          <MetricCard
            title="Total Events"
            value={analyticsData?.summary.totalEvents || 0}
            icon={<Activity className="h-4 w-4" />}
            description="Analytics events tracked"
          />
        </div>
      )}

      {/* Analytics Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="content">Content</TabsTrigger>
          <TabsTrigger value="engagement">Engagement</TabsTrigger>
          <TabsTrigger value="system">System</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* User Growth Chart */}
            <UserGrowthChart data={analyticsData?.userGrowth || []} />

            {/* Stream Views Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 truncate">
                  <Play className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">Stream Views</span>
                </CardTitle>
                <CardDescription className="truncate">
                  Daily view counts this week
                </CardDescription>
              </CardHeader>
              <CardContent>
                  <div className="h-64 flex items-center justify-center bg-muted/20 rounded">
                    <div className="text-center">
                      <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                      <p className="text-muted-foreground">Stream view trends</p>
                      <p className="text-sm text-muted-foreground mt-2">
                        {analyticsData?.summary.streamViews ? `${analyticsData.summary.streamViews} total views` : 'No stream data'}
                      </p>
                    </div>
                  </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Activity */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Recent Activity
              </CardTitle>
              <CardDescription>
                Latest platform activity and events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-primary rounded-full" />
                    <span className="text-sm">Analytics tracking active</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {analyticsData?.summary.totalEvents || 0}
                    </Badge>
                    <span className="text-xs text-muted-foreground">events</span>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full" />
                    <span className="text-sm">Unique users tracked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {analyticsData?.summary.uniqueUsers || 0}
                    </Badge>
                    <span className="text-xs text-muted-foreground">users</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* User Demographics */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-5 w-5" />
                  Geographic Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analyticsData?.geoDistribution && analyticsData.geoDistribution.length > 0 ? (
                    analyticsData.geoDistribution.slice(0, 5).map((region, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm">{region.country}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-muted rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{ width: `${region.percentage}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">
                            {region.percentage}%
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No geographic data available</p>
                      <p className="text-sm">Data will appear as users access the platform</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Device Usage */}
            <Card>
              <CardHeader>
                <CardTitle>Device Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Desktop</span>
                    <span className="text-sm font-medium">{analyticsData?.deviceStats.desktop || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Mobile</span>
                    <span className="text-sm font-medium">{analyticsData?.deviceStats.mobile || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Tablet</span>
                    <span className="text-sm font-medium">{analyticsData?.deviceStats.tablet || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          {/* Top Performing Content */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 truncate">
                <TrendingUp className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">Top Performing Content</span>
              </CardTitle>
              <CardDescription className="truncate">
                Most viewed streams and highest engagement
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {analyticsData?.topStreams && analyticsData.topStreams.length > 0 ? (
                  analyticsData.topStreams.slice(0, 5).map((stream, index) => (
                    <div key={stream.streamId} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border rounded-lg">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium">{index + 1}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <button
                            onClick={() => navigate(`/streams/${stream.streamId}`)}
                            className="text-left hover:underline"
                          >
                            <h4 className="font-medium truncate">{stream.title}</h4>
                          </button>
                          <p className="text-sm text-muted-foreground">
                            {stream.views.toLocaleString()} views
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/streams/${stream.streamId}`}>
                            View Details
                          </Link>
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No stream view data available</p>
                    <p className="text-sm">Data will appear as users watch streams</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="engagement" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-3">
            <MetricCard
              title="Page Views"
              value={analyticsData?.summary.pageViews || 0}
              icon={<Eye className="h-4 w-4" />}
              description="Total page views"
            />
            <MetricCard
              title="Stream Views"
              value={analyticsData?.summary.streamViews || 0}
              icon={<Play className="h-4 w-4" />}
              description="Stream interactions"
            />
            <MetricCard
              title="Active Users"
              value={analyticsData?.summary.uniqueUsers || 0}
              icon={<Users className="h-4 w-4" />}
              description="Unique users this period"
            />
          </div>
        </TabsContent>

        <TabsContent value="system" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Total Events</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsData?.summary.totalEvents || 0}</div>
                <p className="text-xs text-muted-foreground">Analytics events tracked</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Unique Users</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analyticsData?.summary.uniqueUsers || 0}</div>
                <p className="text-xs text-muted-foreground">Active this period</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Top Country</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsData?.geoDistribution && analyticsData.geoDistribution.length > 0
                    ? analyticsData.geoDistribution[0].country
                    : 'N/A'}
                </div>
                <p className="text-xs text-muted-foreground">Most active region</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Data Period</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{timeRange}</div>
                <p className="text-xs text-muted-foreground">Current time range</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
